import React from 'react'
import { Navigate, Outlet, useLocation } from 'react-router-dom'
import { useAuthentication } from '@auth/hooks'
import { LOGIN_PATH } from '@constants'
import { MkAuthenticationService } from '@auth/services/AuthService/_authService'

interface IAuthGuardProps {
  children?: React.ReactNode
}

export const AuthGuard: React.FC<IAuthGuardProps> = ({ children }) => {
  const { isAuthorized, isAuthInitialized, isSharedSessionSyncInProgress, refreshMakoAuthorizationTokens } = useAuthentication()
  const location = useLocation()

  React.useLayoutEffect(() => {
    if (!isAuthInitialized || !isAuthorized || isSharedSessionSyncInProgress) {
      return
    }
    if (!MkAuthenticationService.areMakoAuthTokensExpired()) {
      return
    }

    refreshMakoAuthorizationTokens?.()?.catch(() => {})
  }, [location.pathname, location.search, isAuthInitialized, isAuthorized, isSharedSessionSyncInProgress, refreshMakoAuthorizationTokens])

  // Wait for auth initialization to complete before making authorization decisions
  if (!isAuthInitialized) {
    return children
  }

  // Defer login redirects while attempting shared-session bootstrap for new tabs.
  if (isSharedSessionSyncInProgress) {
    if (!children) return <Outlet />
    return children
  }

  if (isAuthorized && MkAuthenticationService.areMakoAuthTokensExpired()) {
    return null
  }

  const searchParams = new URLSearchParams(location.search)
  const hasLoginError = searchParams.has('loginError')

  if (!isAuthorized && !hasLoginError) {
    const postLoginReturnPath = `${location.pathname}${location.search}`
    const redirectTo = `${LOGIN_PATH}?redirectTo=${encodeURIComponent(postLoginReturnPath)}`

    return (
      <Navigate
        replace
        state={{ from: location }}
        to={redirectTo}
      />
    )
  }

  // If there's a loginError in the URL, allow rendering to let error handler process it
  if (!isAuthorized && hasLoginError) {
    return children
  }

  if (!children) return <Outlet />
  return children
}
